import { Component, Input, OnInit, Output, EventEmitter, OnChanges, SimpleChanges, ViewChild, ElementRef, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { DefaultRegisterComponentResource, RegisterComponentResources } from './register.component.resources';
import { FormValidators } from '../../../_helpers/form-validators';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { NgOtpInputConfig, NgOtpInputModule } from 'ng-otp-input';
import { BehaviorSubject, interval, Subject, Subscription, takeUntil } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { GenerateOTPAction, GenerateOTPSuccessAction, VerifyOTPAction, VerifyOTPFailureAction, VerifyOTPSuccessAction } from '../../../store/auth/auth.actions';
import { Store } from '@ngrx/store';
import { SnackBarService } from '../../../services/snackbar.service';
import { CommonModule } from '@angular/common';
import { CheckUserPhoneOrEmailAction, CheckUserPhoneOrEmailSuccessAction } from '../../../store/user/user.actions';
import { CountriesStatesService, IPreferredLanguage } from '../../../services/countries_states_service';

export interface UserFormDetails {
  fullName?: string | null | undefined;
  mobileNumber?: string | null | undefined;
  email?: string | null | undefined;
  preferredLanguage?: string | null | undefined;
  IsMarketingOptedIn?: boolean;
}

export enum OtpStatus {
  Initial = "Initial",
  Sent = "Sent",
  Verified = "Verified",
  Verifying = "Verifying",
  Failed = 'Failed'
}

@Component({
  selector: 'app-register',
  standalone: false,
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss']
})
export class RegisterComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('formContainer', { static: true }) formContainer!: ElementRef;

  @Input() formData: UserFormDetails = {};  // Parent-provided data
  @Output() formSubmitted = new EventEmitter<Partial<UserFormDetails>>();  // Emits only changed fields
  @Input() resources: Partial<RegisterComponentResources> = {};
  @Input() allowEmail: boolean = false;
  @Input() allowMobile: boolean = false;

  registerForm: FormGroup;
  preferredLanguagesOptions: IPreferredLanguage[] = [];
  private initialFields: Set<string> = new Set();  // Tracks initially provided fields
  private userChanges: Partial<UserFormDetails> = {};  // Stores user-modified fields
  finalResources!: RegisterComponentResources;
  isFormSubmitted: boolean = false;
  TERMSOFUSEURL = 'https://www.yourbinoculars.com/terms-of-service';
  PRIVACYPOLICYURL = 'https://www.yourbinoculars.com/privacy-policy';

  otpStatus: OtpStatus = OtpStatus.Initial;
  otpVerificationToken: string | null = null;
  private otpStatusSubject?: BehaviorSubject<OtpStatus>;
  private otpVerificationTokenSubject?: BehaviorSubject<string | null>;
  private verifyOtpSubject?: Subject<{ verificationToken: string, userInput: string, receiver: string }>;
  private resendOtpSubject?: Subject<{ receiver: string, deliveryMode: 'SMS' | 'EMAIL' }>;
  private completeSubject?: Subject<void>;
  private isOtpDialogOpen: boolean = false;
  private destroy$ = new Subject<void>();
  private _componentSubscriptions: Subscription[] = [];
  private otpVerificationSuccessToken: string | null = null;
  private expiresInSubject?: BehaviorSubject<number>;
  private originalFieldValue: string | null = null;
  private pendingFieldCheck: { field: string, value: string } | null = null;

  constructor(private fb: FormBuilder, private otpDialog: MatDialog, private actions$: Actions,
    private store: Store, private snackBar: SnackBarService, private countriesStatesService: CountriesStatesService) {
    this.registerForm = this.fb.group({
      fullName: ['', FormValidators.fullNameValidators()],
      email: ['', FormValidators.emailValidators()],
      terms: [false, [Validators.requiredTrue]],
      preferredLanguage: [''],
      IsMarketingOptedIn: [{
        value: true,
      }]
    });

    this.userChanges.IsMarketingOptedIn = true; // ensure it's always emitted

    if (this.allowMobile) {
      this.registerForm.addControl('mobileNumber', this.fb.control('', FormValidators.mobileValidators()));
    }

    Object.keys(this.registerForm.controls).forEach(fieldName => {
      this.registerForm.get(fieldName)?.valueChanges.subscribe(value => {
        if (!this.initialFields.has(fieldName)) {
          this.userChanges[fieldName as keyof UserFormDetails] = value;
        }
      });
    });
  }

  getMobileErrorMessage(): string {
    const mobileControl = this.registerForm.get('mobileNumber');
    if (mobileControl?.errors) {
      if (mobileControl.errors['invalidMobile'] || mobileControl.errors['pattern']) {
        return this.finalResources.mobileNumberInvalidPatternErrMsg;
      }
    }
    return '';
  }

  getEmailErrorMessage(): string {
    const emailControl = this.registerForm.get('email');
    if (emailControl?.errors) {
      if (emailControl.errors['required']) {
        return this.finalResources.emailAddressRequiredErrMsg;
      }
      if (emailControl.errors['email'] || emailControl.errors['pattern']) {
        return this.finalResources.emailAddressInvalidPatternErrMsg;
      }
    }
    return '';
  }

  triggerValidation(controlName: string): void {
    const control = this.registerForm.get(controlName);
    if (control) {
      control.markAsTouched(); // Mark field as touched
      control.updateValueAndValidity({ onlySelf: true }); // Recalculate validity
    }
  }

  ngOnInit(): void {
    this.finalResources = { ...DefaultRegisterComponentResource, ...this.resources };
    this.patchFormWithFormData(this.formData);

    // Set the Preferred Language Options
    this.countriesStatesService.getAllPreferredLanguages().subscribe(preferredLanguages => {
      this.preferredLanguagesOptions = preferredLanguages;

      // Set 'Hindi' as default only if it exists in the options
      const hindiOption = preferredLanguages.find(lang => lang.name === 'Hindi');
      if (hindiOption) {
        this.registerForm.get('preferredLanguage')?.setValue('Hindi', { emitEvent: false });
        this.userChanges.preferredLanguage = 'Hindi'; // ensure it's always emitted
      }
    });

    // Handle sucess action for checking if mobile or email already exists
    this._componentSubscriptions.push(
      this.actions$.pipe(
        ofType(CheckUserPhoneOrEmailSuccessAction),
        takeUntil(this.destroy$)
      ).subscribe(({ response, field }) => {
        if (response.Value) {
          // Field already exists, show error and stop
          const fieldName = field === "email" ? "email address" : "mobile number";
          this.snackBar.showSnackBar("error", `An account with this ${fieldName} already exists. Please use a different one.`);
          this.pendingFieldCheck = null; // Clear pending check
        } else {
          // Field is available, proceed with OTP generation
          if (this.pendingFieldCheck) {
            this.store.dispatch(GenerateOTPAction({
              request: {
                Receiver: this.pendingFieldCheck.value,
                DeliveryMode: this.pendingFieldCheck.field === 'email' ? 'EMAIL' : 'SMS'
              }
            }));
            this.pendingFieldCheck = null; // Clear after dispatching
          }
        }
      })
    );

    // Handle Verify OTP Success
    this._componentSubscriptions.push(
      this.actions$.pipe(
        ofType(VerifyOTPSuccessAction),
        takeUntil(this.destroy$)
      ).subscribe(({ response }) => {
        this.otpVerificationSuccessToken = response.Value.Token;
        if (this.otpStatusSubject) {
          this.otpStatusSubject.next(OtpStatus.Verified);
        }
        this.snackBar.showSnackBar('success', "OTP Verified Successfully.");
      })
    );

    // Handle Verify OTP Failure
    this._componentSubscriptions.push(
      this.actions$.pipe(
        ofType(VerifyOTPFailureAction),
        takeUntil(this.destroy$)
      ).subscribe(({ error }) => {
        if (this.otpStatusSubject) {
          this.otpStatusSubject.next(OtpStatus.Failed);
        }
        this.otpVerificationSuccessToken = null;
      })
    );

    // Handle the Generate OTP Success
    this._componentSubscriptions.push(
      this.actions$.pipe(
        ofType(GenerateOTPSuccessAction),
        takeUntil(this.destroy$)
      ).subscribe(({ response }) => {
        this.otpStatus = OtpStatus.Sent;
        this.otpVerificationToken = response.Value.VerificationToken;
        const expiresIn = response.Value.ExpiresIn || 120;
        if (this.otpStatusSubject) {
          this.otpStatusSubject.next(this.otpStatus);
        }
        if (this.otpVerificationTokenSubject) {
          this.otpVerificationTokenSubject.next(this.otpVerificationToken);
        }
        if (this.expiresInSubject) {
          this.expiresInSubject.next(expiresIn);
        }
        const changedField = this.getChangedField();
        if (!changedField) {
          return;
        }
        this.originalFieldValue = changedField.value; // Store original value so that on dialog close, restore with the token value
        if (!this.isOtpDialogOpen) {
          this.otpStatusSubject = new BehaviorSubject<OtpStatus>(this.otpStatus);
          this.otpVerificationTokenSubject = new BehaviorSubject<string | null>(this.otpVerificationToken);
          this.expiresInSubject = new BehaviorSubject<number>(expiresIn);
          this.verifyOtpSubject = new Subject<{ verificationToken: string, userInput: string, receiver: string }>();
          this.resendOtpSubject = new Subject<{ receiver: string, deliveryMode: 'SMS' | 'EMAIL' }>();
          this.completeSubject = new Subject<void>();

          // Verify OTP Button Click
          const verifySub = this.verifyOtpSubject.subscribe(data => {
            if (this.otpStatusSubject) {
              this.otpStatusSubject.next(OtpStatus.Verifying);
            }
            this.store.dispatch(VerifyOTPAction({
              request: {
                VerificationToken: data.verificationToken,
                UserInput: data.userInput,
                Receiver: data.receiver,
              }
            }));
          });

          // Resend OTP Button Click
          const resendSub = this.resendOtpSubject.subscribe(data => {
            this.store.dispatch(GenerateOTPAction({
              request: {
                Receiver: data.receiver,
                DeliveryMode: data.deliveryMode
              }
            }));
          });

          // Complete Payment Button Click
          const completeSub = this.completeSubject.subscribe(() => {
            const changedField = this.getChangedField();
            if (changedField && this.otpVerificationSuccessToken) {
              const fieldToUpdate = changedField.field === 'mobile number' ? 'mobileNumber' : 'email';
              this.userChanges[fieldToUpdate as keyof Omit<UserFormDetails, 'IsMarketingOptedIn'>] = this.otpVerificationSuccessToken;
            }

            // Transform empty preferredLanguage to null before emitting
            const finalChanges = { ...this.userChanges };

            this.isFormSubmitted = true;
            this.formSubmitted.emit(finalChanges);
            optDialogRef.close();
          });

          // Open the Dialog And Store Its Reference
          const optDialogRef = this.otpDialog.open(RegisterOTPDialogComponent, {
            data: {
              fieldName: changedField.field,
              fieldValue: changedField.value,
              otpStatusSubject: this.otpStatusSubject,
              otpVerificationTokenSubject: this.otpVerificationTokenSubject,
              expiresInSubject: this.expiresInSubject,
              verifyOtpSubject: this.verifyOtpSubject,
              resendOtpSubject: this.resendOtpSubject,
              completeSubject: this.completeSubject
            },
            disableClose: true         // Prevent Dialog From Closing On Clicking Outside the Dialog
          });

          this.isOtpDialogOpen = true;
          // After the Dialog Is Closed
          optDialogRef.afterClosed().subscribe(() => {
            this.isOtpDialogOpen = false;
            verifySub.unsubscribe();
            resendSub.unsubscribe();
            completeSub.unsubscribe();
            // Reset userChanges to original value
            const changedField = this.getChangedField();
            if (changedField && this.originalFieldValue) {
              const fieldToUpdate = changedField.field === 'mobile number' ? 'mobileNumber' : 'email';
              this.userChanges[fieldToUpdate as keyof Omit<UserFormDetails, 'IsMarketingOptedIn'>] = this.originalFieldValue;
            }
            this.originalFieldValue = null; // Clear stored value
          });
        }
      })
    );

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this._componentSubscriptions.forEach((sub) => sub.unsubscribe());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.isFormSubmitted && changes['formData'] && !changes['formData'].firstChange) {
      this.patchFormWithFormData(changes['formData'].currentValue);
    }

    if (changes['allowMobile']) {
      const mobileControlName = 'mobileNumber';
      if (this.allowMobile && !this.registerForm.get(mobileControlName)) {
        // Add the control if allowMobile is true and it doesn't exist
        this.registerForm.addControl(mobileControlName, this.fb.control('', FormValidators.mobileValidators()));
        // Subscribe to valueChanges for the new control
        this.registerForm.get(mobileControlName)?.valueChanges.subscribe(value => {
          if (!this.initialFields.has(mobileControlName)) {
            this.userChanges[mobileControlName as keyof UserFormDetails] = value;
          }
        });
      } else if (!this.allowMobile && this.registerForm.get(mobileControlName)) {
        // Remove the control if allowMobile is false and it exists
        const currentValue = this.registerForm.get(mobileControlName)?.value;
        this.registerForm.removeControl(mobileControlName);
        // Optionally preserve value in userChanges if needed
        if (currentValue && !this.initialFields.has(mobileControlName)) {
          this.userChanges[mobileControlName as keyof UserFormDetails] = currentValue;
        }
      }
      this.registerForm.updateValueAndValidity(); // Recalculate form validity
    }
  }

  private patchFormWithFormData(formData: UserFormDetails) {
    if (formData) {
      this.initialFields.clear();  // Reset tracking

      const patchValue: any = {};
      Object.keys(formData).forEach((key) => {
        if (formData[key as keyof UserFormDetails] != null) {
          patchValue[key] = formData[key as keyof UserFormDetails];
          this.initialFields.add(key);  // Track initially provided fields
        }
      });

      this.registerForm.patchValue(patchValue, { emitEvent: false });

      this.initialFields.forEach(field => this.registerForm.get(field)?.disable());

      setTimeout(() => this.setFocusOnFirstEnabledInput(), 0);
    }
  }

  // Get the Changed Fields From Email and Mobile
  private getChangedField(): { field: string, value: string } | null {
    if (this.userChanges.mobileNumber && !this.initialFields.has('mobileNumber')) {
      return { field: 'mobile number', value: this.userChanges.mobileNumber }
    }
    if (this.userChanges.email && !this.initialFields.has('email')) {
      return { field: 'email', value: this.userChanges.email }
    }
    return null;
  }

  private setFocusOnFirstEnabledInput() {
    if (this.formContainer) {
      const inputs = this.formContainer.nativeElement.querySelectorAll('input:not([disabled])');
      if (inputs.length > 0) {
        (inputs[0] as HTMLInputElement).focus();
      }
    }
  }

  onSubmit() {
    if (this.registerForm.valid) {
      const changedField = this.getChangedField();
      if (changedField) {
        // Store for later use
        this.pendingFieldCheck = changedField;

        // Check if field already exists before generating OTP
        const fieldType = changedField.field === "email" ? "email" : "mobile";
        this.store.dispatch(CheckUserPhoneOrEmailAction({
          mobileOrEmail: changedField.value,
          field: fieldType
        }));
      } else {
        // Transform empty preferredLanguage to null before emitting
        const finalChanges = { ... this.userChanges };
        this.isFormSubmitted = true;
        this.formSubmitted.emit(finalChanges);  // Emit only user-modified fields
      }
    }
  }
}

// OTP Component For Register Page
@Component({
  selector: 'register-otp-dialog',
  template: `
    <div class="otp-dialog-container">
        <div class="header">
            <h4 class="header-title">Verify Your Changes</h4>
            <p class="header-description">
                We need to verify your new {{data.fieldName}} for security purposes.
            </p>
        </div>

        <mat-dialog-content>
            <div  class="verification-block">
                <div class="section-title">
                    <mat-icon>{{data.fieldName === 'email' ? 'email': 'phone'}}</mat-icon>
                    <span>{{data.fieldName === 'email' ? 'Email Verification': 'Mobile Verification'}}</span>
                    <span class="sent-label">
                        {{localOtpStatus}}
                    </span>
                </div>
                <div class="description">
                    Enter the 6-digit code sent to <strong>{{data.fieldName === 'email' ? '': '+91 '}} {{ data.fieldValue }}</strong>
                </div>

                  <div class="otp-boxes">
                    <ngx-otp-input #ngxOtpInput [config]="config" (onInputChange)="onOtpChange($event)"
                      [disabled]="localOtpStatus === OTPStatusEnum.Verified"
                      (keydown)="onOtpKeydown($event)"
                      [ngModel]="otpString || ''">
                    </ngx-otp-input>
                  </div>

                <div class="resend-block">
                  <span *ngIf="expiresIn > 0"
                   [style.visibility]="localOtpStatus !== OTPStatusEnum.Verified ? 'visible' : 'hidden'"
                   >Expires In {{ expiresIn }} seconds</span>
                  <button *ngIf="expiresIn === 0" mat-raised-button
                   [disabled]="localOtpStatus === OTPStatusEnum.Verifying || localOtpStatus === OTPStatusEnum.Verified"
                   (click)="resendOTP()">Resend OTP</button>
                </div>
                <button mat-raised-button color="primary" class="verify-btn"
                     [ngClass]="{'verify-btn-disabled': !isOtpFilled() ||
                    localOtpStatus === OTPStatusEnum.Verifying || localOtpStatus === OTPStatusEnum.Verified}"
                    [disabled]="!isOtpFilled() ||
                    localOtpStatus === OTPStatusEnum.Verifying || localOtpStatus === OTPStatusEnum.Verified" (click)="verifyOtp()">
                    {{ localOtpStatus === OTPStatusEnum.Verifying ? 'Verifying...' : 'Verify' }}
                </button>
            </div>
        </mat-dialog-content>

        <mat-dialog-actions class="actions">
            <button class="cancel-button" mat-stroked-button mat-dialog-close
            [disabled]="localOtpStatus === OTPStatusEnum.Verified"
            [ngClass]="{'cancel-button-disabled': localOtpStatus === OTPStatusEnum.Verified}"
            (click)="onCancel()">Cancel</button>
           <button #completeButton class="complete-button" mat-stroked-button
              [ngClass]="{'complete-btn-disabled': localOtpStatus !== OTPStatusEnum.Verified}"
              [disabled]="localOtpStatus !== OTPStatusEnum.Verified"
              (click)="onCompleteVerification()">
              Complete Verification
            </button>
        </mat-dialog-actions>
    </div>
  `,
  standalone: true,
  imports: [MatDialogActions, MatDialogContent, MatIcon, NgOtpInputModule, FormsModule, CommonModule],
  styles: [`
    .otp-dialog-container {
      padding: 16px 8px;
      border-radius: 16px;
      background-color: rgba(255, 255, 255, 1);
      min-width: 500px;

      hr {
          margin: 0;
      }

      .header {
          padding: 10px 10px 0 10px;

          .header-title {}

          .header-description {
              color: #717182;
              font-size: 14px;
              margin: 0px;
          }
      }

      mat-dialog-content {
          padding: 0 10px 0 10px;
      }

      mat-dialog-actions {
          padding: 0 10px;
      }

      .subtitle {
          font-size: 14px;
          margin-top: -10px;
          margin-bottom: 10px;
          color: #666;
      }

      .verification-block {
          margin: 0;
          margin-top: 10px;
          position: relative;

          .section-title {
              display: flex;
              align-items: center;
              font-weight: bold;
              gap: 8px;

              .sent-label {
                  background-color: #e6f4ff;
                  color: #1a73e8;
                  border-radius: 4px;
                  padding: 2px 6px;
                  font-size: 12px;
                  margin-left: auto;
              }
          }

          .description {
              font-size: 13px;
              margin-top: 4px;
              margin-bottom: 10px;
          }

          .otp-boxes {
              display: flex;
              gap: 8px;
              justify-content: center;
              margin-bottom: 20px;

              input {
                  width: 40px;
                  height: 48px;
                  text-align: center;
                  font-size: 20px;
                  border: 1px solid #ccc;
                  border-radius: 6px;
              }
          }

          .resend-block {
              font-size: 12px;
              color: gray;
              margin-top: 10px;
              margin-bottom: 10px;
              width: fit-content;

              button {
                  padding: 5px;
                  background-color: transparent;
                  border: 1px solid rgba(0, 0, 0, 0.1);
                  border-radius: 4px;
              }

              button: disabled {
                pointer-events: none;
                opacity: 0.6;
              }
          }

          .verify-btn {
              position: absolute;
              right: 0;
              bottom: 0;
              border-radius: 4px;
              background-color: rgba(249, 175, 99, 1);
              color: white;
              padding: 5px 10px;
              border: none;
          }

          .verify-btn-disabled {
            opacity: 0.5 !important;
            cursor: not-allowed !important;
          }
      }

      .actions {
          width: 100%;

          button {
              width: calc(50% - 5px);
              margin: 0;
              border-radius: 4px;
              font-size: 12px;
          }

          .cancel-button {
              margin-right: 10px;
              color: black;
              border: 1px solid rgba(0, 0, 0, 0.1);
              background-color: transparent;
              padding: 5px;
          }

          .cancel-button-disabled {
            opacity: 0.5 !important;
            cursor: not-allowed !important;
          }

          .complete-button {
            background-color: #3b3a3a;
            color: white;
            padding: 5px;
            border: 1px solid rgba(0, 0, 0, 0.1);
          }

          .complete-btn-disabled {
            opacity: 0.5 !important;
            cursor: not-allowed !important;
          }
      }
    }
  `]
})
export class RegisterOTPDialogComponent implements OnDestroy {
  @ViewChild('ngxOtpInput', { static: false }) ngxOtpInput: any;
  @ViewChild('completeButton', { static: false }) completeButton!: ElementRef;
  config: NgOtpInputConfig = {
    allowNumbersOnly: true,
    length: 6,
    isPasswordInput: false,
    disableAutoFocus: false,
    placeholder: '',
    separator: '',
  };

  otpString: string | null = null;
  OTPStatusEnum = OtpStatus;
  expiresIn: number = 120;
  localOtpStatus: OtpStatus;
  localOtpVerificationToken: string | null = null;
  private timerSubscription?: Subscription;

  onOtpChange(otp: string | null): void {
    this.otpString = otp;
  }

  constructor(
    public dialogRef: MatDialogRef<RegisterOTPDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      fieldName: string,
      fieldValue: string,
      otpStatusSubject: BehaviorSubject<OtpStatus>,
      otpVerificationTokenSubject: BehaviorSubject<string | null>,
      expiresInSubject: BehaviorSubject<number>,
      verifyOtpSubject: Subject<{ verificationToken: string, userInput: string, receiver: string }>,
      resendOtpSubject: Subject<{ receiver: string, deliveryMode: 'SMS' | 'EMAIL' }>,
      completeSubject: Subject<void>
    },
    private store: Store
  ) {
    this.localOtpStatus = this.data.otpStatusSubject.value;
    this.localOtpVerificationToken = this.data.otpVerificationTokenSubject.value;
    this.expiresIn = this.data.expiresInSubject.value;
    this.data.otpStatusSubject.subscribe(status => {
      this.localOtpStatus = status;
      if (status === OtpStatus.Verified && this.completeButton) {
        this.completeButton.nativeElement.focus();
      }
    });
    this.data.otpVerificationTokenSubject.subscribe(token => {
      this.localOtpVerificationToken = token;
    });
    this.data.expiresInSubject.subscribe(expiresIn => {
      this.expiresIn = expiresIn;
      this.startTimer();
    });
    this.startTimer();
    setTimeout(() => {
      if (this.ngxOtpInput) {
        const eleId = this.ngxOtpInput.getBoxId(0);
        this.ngxOtpInput.focusTo(eleId);
      }
    }, 1000);
  }

  // Start the Resend OTP Timer
  private startTimer(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    this.timerSubscription = interval(1000).subscribe(() => {
      if (this.expiresIn > 0) {
        this.expiresIn--;
      } else {
        this.timerSubscription?.unsubscribe();
      }
    });
  }

  // On Destroy, Clear All the Subscriptions
  ngOnDestroy(): void {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  // Handle the KeyDown on the OTP Input Component
  onOtpKeydown(event: KeyboardEvent): void {
    // When the OTP Is filled, pressing enters dispatches the verify otp action
    if (event.key === 'Enter' && this.isOtpFilled() && this.localOtpStatus !== OtpStatus.Verifying && this.localOtpStatus !== OtpStatus.Verified) {
      this.verifyOtp();
    }
  }

  // Handle Verify OTP Case
  verifyOtp() {
    if (!this.localOtpVerificationToken || !this.otpString) {
      return;
    }
    this.data.verifyOtpSubject.next({
      verificationToken: this.localOtpVerificationToken,
      userInput: this.otpString,
      receiver: this.data.fieldValue,
    });
  }

  // Resend OTP Click
  resendOTP() {
    if (this.expiresIn === 0) {        // Only Send the OTP When the Expire Time Is 0
      this.data.resendOtpSubject.next({
        receiver: this.data.fieldValue,
        deliveryMode: this.data.fieldName === 'email' ? 'EMAIL' : 'SMS'
      });
    }
  }

  // Check If the OTP Field Is Completed
  isOtpFilled(): boolean {
    return this.otpString ? this.otpString.length === 6 : false;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  // Handle the On Complete Verification Subject
  onCompleteVerification(): void {
    this.data.completeSubject.next();
  }

  onConfirm(): void {
    this.dialogRef.close(true);
  }
}
